import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  searchMqProjectsHeaders,
  isMobile,
  paginationOptions,
  maxSelectProjectCount,
  searchMqProjectsMobileSort,
} from "utils/common";
import { useSortableTable } from "components/common/desktopDataTable/useSortableTable";
import Button from "react-bootstrap/Button";
import SelectInput from "common/selectInput";
import Pagination from "common/pagination";
import FloorMapCard from "components/siteRepo/common/floorMapCard";
import CustomTooltip from "common/tooltip";
import MobileSort from "common/mobileSortField";
import { getRoomsByMqProject, selectAllProjects, getLayerImage } from "store/siteRepo/actionCreator";
import { updateSelectLayer, updateSelectProject } from "store/slices/siteRepo";
import downArrowSmall from "svg/downArrowSmall.svg";
import upArrowSmall from "svg/upArrowSmall.svg";

const SearchTable = () => {
  const dispatch = useDispatch();
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [currentButton, setCurrentButton] = useState(1);
  const [arrOfCurrentButton, setArrOfCurrentButton] = useState([]);
  const [mobileView, setMobileView] = useState(false);
  const [sortValue, setSortValue] = useState("");

  const { searchMqProjectsResults } = useSelector((state) => state.siteRepo.createNewSite.searchMqProjects);
  const { selectedData } = useSelector((state) => state.siteRepo.createNewSite);
  const { items, requestSort, sortConfig } = useSortableTable(searchMqProjectsResults, searchMqProjectsResults);
  const { dataKey, order } = sortConfig;

  const accordionParentRef = useRef();
  const mmobileSortRef = useRef();

  const tableData = items;

  const numberOfPages = [];
  let currentData = [...tableData];
  let howManyPages = 0;

  const indexOfLastPost = currentButton * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;

  currentData = tableData && tableData.slice(indexOfFirstPost, indexOfLastPost);
  if (tableData) {
    howManyPages = Math.ceil(tableData.length / postsPerPage);
  }
  for (let i = 1; i <= howManyPages; i++) {
    numberOfPages.push(i);
  }

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) {
        setMobileView(false);
      } else {
        setMobileView(true);
      }
    };
    window.addEventListener("resize", handleResize);
    if (isMobile()) {
      handleResize();
    }
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileView, postsPerPage]);

  useEffect(() => {
    let tempNumberOfPages = [...arrOfCurrentButton];
    let dotsInitial = "...";
    let dotsLeft = "...";
    let dotsRight = "...";
    if (numberOfPages.length === 0 || numberOfPages.length === 1) {
      tempNumberOfPages = [1];
    } else if (numberOfPages.length === 2) {
      tempNumberOfPages = [1, 2];
    } else if (numberOfPages.length === 3) {
      tempNumberOfPages = [1, 2, 3];
    } else if (numberOfPages.length === 4) {
      tempNumberOfPages = [1, 2, 3, 4];
    } else {
      if (currentButton >= 1 && currentButton <= 3) {
        tempNumberOfPages = [1, 2, 3, dotsInitial, numberOfPages.length];
      } else if (currentButton === 4) {
        const sliced = numberOfPages.slice(0, 4);
        tempNumberOfPages = [...sliced, dotsInitial, numberOfPages.length];
      } else if (currentButton > 4 && currentButton < numberOfPages.length - 2) {
        const sliced1 = numberOfPages.slice(currentButton - 2, currentButton);
        const sliced2 = numberOfPages.slice(currentButton, currentButton + 1);
        tempNumberOfPages = [1, dotsLeft, ...sliced1, ...sliced2, dotsRight, numberOfPages.length];
      } else if (currentButton > numberOfPages.length - 3) {
        const sliced = numberOfPages.slice(numberOfPages.length - 4);
        tempNumberOfPages = [1, dotsLeft, ...sliced];
      } else if (currentButton === dotsInitial) {
        setCurrentButton(arrOfCurrentButton[arrOfCurrentButton.length - 3] + 1);
      } else if (currentButton === dotsRight) {
        setCurrentButton(arrOfCurrentButton[3] + 2);
      } else if (currentButton === dotsLeft) {
        setCurrentButton(arrOfCurrentButton[3] - 2);
      }
    }
    setArrOfCurrentButton(tempNumberOfPages);
  }, [currentButton, postsPerPage, searchMqProjectsResults]);

  useEffect(() => {
    if (document.querySelectorAll(".accordion-content-container.show").length) {
      document
        .querySelectorAll(
          `button[data-bs-target="#${document.querySelectorAll(".accordion-content-container.show")[0].id}"]`,
        )[0]
        ?.classList.add("collapsed");
      document.querySelectorAll(".accordion-content-container.show")[0]?.classList.remove("show");
    }
  }, [currentButton]);

  const handleSortValues = (e, value) => {
    setSortValue(value);
  };

  const handlePrevious = () => {
    setCurrentButton((prev) => (prev === 1 ? prev : prev - 1));
  };
  const handleNext = () => {
    setCurrentButton((prev) => (prev === numberOfPages.length ? prev : prev + 1));
  };

  const handleResultsPerPage = (e, count) => {
    const value = count?.description ? count.description : 10;
    setPostsPerPage(value);
    setCurrentButton(1);
  };

  const handleExpandRow = (mprojectId, elementIndex) => {
    if (!searchMqProjectsResults[elementIndex]?.layersInfo?.length) {
      dispatch(getRoomsByMqProject(mprojectId, elementIndex)).then((res) => {
        dispatch(getLayerImage([{ mprojectId, elementIndex, layersInfo: res }]));
      });
    }
  };

  const onSortingChange = (field) => {
    requestSort(field);
    if (document.querySelectorAll(".accordion-content-container.show").length) {
      document
        .querySelectorAll(
          `button[data-bs-target="#${document.querySelectorAll(".accordion-content-container.show")[0].id}"]`,
        )[0]
        ?.classList.add("collapsed");
      document.querySelectorAll(".accordion-content-container.show")[0]?.classList.remove("show");
    }
  };

  const handleSelection = (e, selectionData) => {
    const { type, data } = selectionData;
    if (type === "layer") {
      dispatch(
        updateSelectLayer({ selectLayerProjectIndex: data.projectIndex, selectLayerLayerIndex: data.layerIndex }),
      );
    } else if (type === "project") {
      if (searchMqProjectsResults[data.projectIndex]?.layersInfo.length === 0) {
        dispatch(getRoomsByMqProject(data.mprojectId, data.projectIndex, true)).then((res) => {
          dispatch(
            getLayerImage([
              { mprojectId: data.mprojectId, elementIndex: data.projectIndex, layersInfo: res, domId: data.domId },
            ]),
          );
        });
      } else {
        setTimeout(() => dispatch(updateSelectProject({ selectProjectIndex: data.projectIndex })), 0);
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedData.isSelectAll) {
      dispatch(selectAllProjects([], true));
    } else {
      const selectedItems = selectedData.projects?.length ? [...selectedData.projects] : [];
      for (var index = 0; index < tableData.length; index++) {
        if (selectedItems.length >= maxSelectProjectCount) break;
        if (!tableData[index].isSelected) {
          selectedItems.push(tableData[index]);
        }
      }
      dispatch(selectAllProjects(selectedItems, false)).then((resp) => {
        setCurrentButton(1);
        dispatch(getLayerImage(resp.map((project) => project.value)));
      });
    }
  };

  return (
    <div className="col-12 search-table">
      <div className="search-form-container__title d-flex align-items-center">
        <span className="search-form-container__title--text w-auto">Project list</span>
        <span className="search-form-container__result-count">{searchMqProjectsResults.length}</span>
      </div>
      {currentData.length > 0 && (
        <div className="col-12 header-container d-none d-md-flex align-items-center flex-column flex-md-row">
          {searchMqProjectsHeaders.map((header, index) => {
            if (header.id === 1) {
              return (
                <div className={header.class} key={index}>
                  <input
                    type="checkbox"
                    className="select-row-checkbox"
                    key={index}
                    onChange={(e) => handleSelectAll(e)}
                    checked={selectedData.isSelectAll}
                    disabled={selectedData.projects?.length >= maxSelectProjectCount && !selectedData.isSelectAll}
                  />
                </div>
              );
            }

            if (header.sortable) {
              return (
                <div className={header.class} key={index}>
                  <div
                    className="filter-item d-flex"
                    onClick={() => (header.sortable ? onSortingChange(header.dataKey) : null)}
                  >
                    <span key={index}>{header.name}</span>
                    {dataKey && dataKey === header.dataKey ? (
                      <img src={`${order === "asc" ? upArrowSmall : downArrowSmall}`} />
                    ) : header.sortable ? (
                      <div className="d-flex flex-column gap-2 pt-1">
                        <img className="sort-icon" src={upArrowSmall} />
                        <img className="sort-icon" src={downArrowSmall} />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            } else {
              return "";
            }
          })}
        </div>
      )}
      {currentData.length > 0 && (
        <div className="d-md-none d-block">
          <MobileSort
            inputRef={mmobileSortRef}
            options={searchMqProjectsMobileSort}
            label="Sort by"
            defaultValue="Select a Type"
            value={sortValue}
            sorting={sortConfig}
            onSorting={onSortingChange}
            handleSelectInput={(e, value) => handleSortValues(e, value)}
          />
        </div>
      )}
      <div className="accordion-items-container">
        <div
          className="col-12 accordion accordion-flush"
          id="search-mq-results-accordion-parent"
          ref={accordionParentRef}
        >
          {currentData.length > 0 ? (
            currentData.map((project, index) => (
              <div className="accordion-item" key={index}>
                <div className="accordion-row d-flex py-4 py-md-0 align-items-center py-lg-0">
                  <div className="item justify-content-center">
                    <input
                      type="checkbox"
                      className="select-row-checkbox"
                      onChange={(e) =>
                        handleSelection(e, {
                          type: "project",
                          data: {
                            projectIndex: project.elementIndex,
                            mprojectId: project.mprojectId,
                            domId: project.domId,
                          },
                        })
                      }
                      checked={project.isSelected}
                      disabled={selectedData.projects?.length >= maxSelectProjectCount && !project.isSelected}
                    />
                  </div>
                  <div className="col-auto col-md-3 item align-items-md-start align-items-center">
                    <span className="item__label project-name d-none d-md-flex">Project Name</span>
                    <CustomTooltip placement="top" text={project.name}>
                      <div className="item__value projName">{project.name}</div>
                    </CustomTooltip>
                  </div>
                  <div className="col-3 item d-none d-md-flex">
                    <span className="item__label">Owner</span>
                    <div className="item__value">{project.ownerM2Id}</div>
                  </div>
                  <div className="item version d-none d-md-flex">
                    <span className="item__label">Version</span>
                    <div className="item__value">{project.revision}</div>
                  </div>
                  <div className="col-2 item d-none d-md-flex">
                    <span className="item__label">Last Updated</span>
                    <div className="item__value">
                      {new Date(project.lastUpdatedOn * 1000).toLocaleDateString("en-US")}
                    </div>
                  </div>
                  <div className="d-lg-flex col-lg-1 item d-none d-md-flex">
                    <span className="item__label">Floors</span>
                    <div className="item__value">
                      <CustomTooltip placement="top" text={"Expand row to view floors"}>
                        View Floors
                      </CustomTooltip>
                    </div>
                  </div>
                  <div className="item justify-content-center expand d-none d-md-flex">
                    <Button
                      className="accordion-button collapsed text-decoration-none"
                      variant="link"
                      data-bs-toggle="collapse"
                      data-bs-target={`#flush-collapse-${project.mprojectId}-${project.lastUpdatedOn}-${index}`}
                      aria-expanded="false"
                      aria-controls={`flush-collapse-${project.mprojectId}-${project.lastUpdatedOn}-${index}`}
                      onClick={() => handleExpandRow(project.mprojectId, project.elementIndex)}
                    ></Button>
                  </div>
                </div>
                <div className="d-flex flex-column d-md-none border-top accordion-row-mobile">
                  <div className="d-flex justify-content-between pb-4 item">
                    <span className="item__label">Owner</span>
                    <span className="item__value">{project.ownerM2Id}</span>
                  </div>
                  <div className="d-flex justify-content-between pb-4 item">
                    <span className="item__label">Version</span>
                    <span className="item__value">{project.revision}</span>
                  </div>
                  <div className="d-flex justify-content-between pb-4 item">
                    <span className="item__label">Last Updated</span>
                    <span className="item__value">{project.lastUpdatedOn}</span>
                  </div>
                  <div className="d-flex justify-content-between item">
                    <span className="item__label">Floors</span>
                    <span className="item__value">View Floors</span>
                  </div>
                </div>
                <div
                  id={`flush-collapse-${project.mprojectId}-${project.lastUpdatedOn}-${index}`}
                  className="accordion-content-container accordion-collapse collapse"
                  aria-labelledby="flush-headingOne"
                  data-bs-parent="#search-mq-results-accordion-parent"
                >
                  <div className="d-flex flex-wrap floormap-container">
                    {project.layersInfo?.length
                      ? project.layersInfo.map((layerItem, index) => (
                          <FloorMapCard
                            key={index}
                            layerIndex={layerItem.elementIndex}
                            handleSelection={handleSelection}
                            project={project}
                            isCheckboxDisabled={
                              selectedData.projects?.length >= maxSelectProjectCount && !project.isSelected
                            }
                            isSelectable={true}
                          />
                        ))
                      : ""}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <React.Fragment>
              <div className="no-results--text text-center">No results found</div>
              <div className="no-results--info text-center">
                Try adjusting your search or filter to what you are looking for
              </div>
            </React.Fragment>
          )}
        </div>
      </div>

      <div className={"section-two border-bottom-0"}>
        <div className="d-flex justify-content-between section-two__root">
          <div className="d-flex search-pagination">
            <div className="pagination__results">
              <div className="d-flex pagination__results__wrapper">
                <div className="pagination__results__heading">Results per page</div>
                <SelectInput
                  options={paginationOptions}
                  value={postsPerPage}
                  showEmptyValue={true}
                  type={"__pagination"}
                  handleSelectInput={(e, value) => handleResultsPerPage(e, value)}
                />
              </div>
            </div>
            <Pagination
              data={searchMqProjectsResults}
              numberOfPages={numberOfPages}
              setCurrentButton={setCurrentButton}
              currentButton={currentButton}
              arrOfCurrentButton={arrOfCurrentButton}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchTable;
